import React, { useState } from 'react';
import { RotateCw, Eye, Sparkles } from 'lucide-react';
import { useShop } from '../context/ShopContext';

export const TiltMugViewer: React.FC = () => {
  const { t } = useShop();
  // tiltAngle from 0 to 90 degrees
  const [tiltAngle, setTiltAngle] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'straight' | 'tilted'>('straight');

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setTiltAngle(val);
    setActiveTab(val > 45 ? 'tilted' : 'straight');
  };

  const setView = (view: 'straight' | 'tilted') => {
    setActiveTab(view);
    setTiltAngle(view === 'tilted' ? 90 : 0);
  };

  // Crossfade opacity based on tilt angle
  const tiltedOpacity = Math.min(1, Math.max(0, tiltAngle / 90));
  const straightOpacity = 1 - tiltedOpacity;

  return (
    <div className="flex flex-col items-center w-full">
      {/* Viewer Canvas Container */}
      <div className="relative w-full max-w-md aspect-square bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden group">
        {/* Subtle background glow */}
        <div className="absolute inset-0 bg-gradient-to-tr from-pink-50/50 via-transparent to-stone-50/50 pointer-events-none" />

        {/* Stage Container */}
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Straight Image - rotates from 0° to 90° as tilt increases */}
          <img
            src="/images/mug-straight.jpg"
            alt="Tasse vue droite - motif géométrique nordique"
            className="absolute max-w-full max-h-full object-contain select-none transition-all duration-300 ease-out"
            style={{
              opacity: straightOpacity,
              transform: `rotate(${tiltAngle}deg) scale(${1 - (tiltAngle / 90) * 0.04})`,
              transformOrigin: 'center center',
              pointerEvents: straightOpacity > 0.5 ? 'auto' : 'none',
            }}
            loading="eager"
          />

          {/* Tilted Image - ends at 0° (its native orientation with handle pointing DOWN) at 90° tilt */}
          <img
            src="/images/mug-tilted.jpg"
            alt="Tasse vue inclinée à 90° - anse vers le bas, message FUCK THIS SHIT révélé"
            className="absolute max-w-full max-h-full object-contain select-none transition-all duration-300 ease-out"
            style={{
              opacity: tiltedOpacity,
              transform: `rotate(${tiltAngle - 90}deg) scale(${0.96 + (tiltAngle / 90) * 0.04})`,
              transformOrigin: 'center center',
              pointerEvents: tiltedOpacity >= 0.5 ? 'auto' : 'none',
            }}
            loading="eager"
          />
        </div>

        {/* Floating badge for secret message */}
        {tiltAngle >= 75 && (
          <div className="absolute top-4 right-4 bg-neutral-950 text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 animate-bounce">
            <Sparkles className="w-3.5 h-3.5 text-pink-400" />
            <span>FUCK THIS SHIT</span>
          </div>
        )}
      </div>

      {/* View Switch Buttons */}
      <div className="flex items-center gap-2 mt-4 bg-gray-100 p-1 rounded-xl w-full max-w-md">
        <button
          onClick={() => setView('straight')}
          className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-all ${
            activeTab === 'straight' && tiltAngle < 45
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <Eye className="w-3.5 h-3.5" />
          <span>{t.hero.straightView}</span>
        </button>

        <button
          onClick={() => setView('tilted')}
          className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-all ${
            activeTab === 'tilted' || tiltAngle >= 45
              ? 'bg-neutral-900 text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <RotateCw className="w-3.5 h-3.5 text-pink-400" />
          <span>{t.hero.tiltedView}</span>
        </button>
      </div>

      {/* Interactive Tilt Slider */}
      <div className="w-full max-w-md mt-4 bg-white p-3.5 rounded-2xl border border-gray-100 shadow-xs">
        <div className="flex items-center justify-between text-xs font-medium text-gray-600 mb-2">
          <span className="flex items-center gap-1">
            <RotateCw className="w-3.5 h-3.5 text-pink-500" />
            {t.hero.tiltHint}
          </span>
          <span className="font-mono text-gray-900 font-semibold">{tiltAngle}°</span>
        </div>
        <input
          type="range"
          min="0"
          max="90"
          value={tiltAngle}
          onChange={handleSliderChange}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pink-500"
          aria-label="Angle d'inclinaison de la tasse"
        />
        <div className="flex justify-between text-[10px] text-gray-400 font-medium mt-1">
          <span>0° (Verticale)</span>
          <span>45°</span>
          <span>90° (Anse vers le bas - Révélé)</span>
        </div>
      </div>
    </div>
  );
};
